import {
  DEFAULT_APP_PRELOAD_DURATION,
  DEFAULT_FILE_PRELOAD_DURATION,
  DEFAULT_LINK_PRELOAD_DURATION,
  DEFAULT_SITE_PRELOAD_DURATION,
} from "../../../../../constants";
import { PulseItem } from "../../../../../store/pulse/types";
import { TimelineItem } from "../../../../../store/timelines/types";

/**
 * Get default preload duration for a content type
 */
export function getPreloadDurationByContentType(
  contentType: string
): number | undefined {
  switch (contentType) {
    case "app":
    case "pulse":
      return DEFAULT_APP_PRELOAD_DURATION;
    case "file":
      return DEFAULT_FILE_PRELOAD_DURATION;
    case "link":
      return DEFAULT_LINK_PRELOAD_DURATION;
    case "site":
      return DEFAULT_SITE_PRELOAD_DURATION;
    case "channel":
    case "playlist":
      // Don't preload these items
      return undefined;
    default:
      return undefined;
  }
}

/**
 * Calculate when preloading should start for dynamic content.
 * Returns the time in milliseconds from the start of the current item when preloading should begin.
 *
 * Formula: currentItemDuration - Math.min(currentItemDuration / 2, defaultPreloadDurationForNextItemType)
 *
 * @param currentBpItem - Currently playing breakpoint item
 * @param nextBpItem - Next breakpoint item to preload
 * @returns Time in ms from current item start when preloading should begin, or undefined if no preloading needed
 */
export function calculateDynamicPreloadDuration(
  currentBpItem: TimelineItem | undefined,
  nextBpItem: TimelineItem | undefined,
  pulseItemForZone: PulseItem | undefined
): number | undefined {
  if (!currentBpItem || !nextBpItem) {
    return undefined;
  }

  const currentDuration = currentBpItem.fullDurationMs;
  if (!currentDuration || currentDuration <= 0) {
    return undefined;
  }

  // Get the default preload duration for the next item's content type
  const defaultPreloadDuration = getPreloadDurationByContentType(
    nextBpItem.type
  );
  if (!defaultPreloadDuration) {
    // Next item type doesn't support preloading
    return undefined;
  }
  // Special case: if current item is pulse, set shouldPreloadIn to 10 seconds
  if (currentBpItem.type === "pulse" && pulseItemForZone) {
    const shouldPreloadInMs = pulseItemForZone.playbackWillFinishInMs / 2; // half of the pulse item duration in milliseconds
    // Only return if it's less than the current duration (makes sense timing-wise)
    const finalPreloadDuration =
      shouldPreloadInMs > DEFAULT_APP_PRELOAD_DURATION
        ? shouldPreloadInMs
        : DEFAULT_APP_PRELOAD_DURATION;
    return finalPreloadDuration;
  }

  // Calculate actual preload duration: min(currentDuration / 2, defaultPreloadDuration)
  const actualPreloadDuration = Math.min(
    currentDuration / 2,
    defaultPreloadDuration
  );
  // Calculate when to start preloading (from start of current item)
  const shouldPreloadInMs = currentDuration - actualPreloadDuration;
  // Only return if it's a positive value (makes sense timing-wise)
  return shouldPreloadInMs > 0 ? shouldPreloadInMs : undefined;
}
